import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import ProductItem from "../components/UI/ProductItem";
import { ProductSkeletonGrid } from "../components/Loaders/ProductSkeleton";
import TrustBar from "../components/UI/TrustBar";
import PackagingSection from "../components/UI/PackagingSection";
import ReviewsSection from "../components/UI/ReviewsSection";
import InstagramSection from "../components/UI/InstagramSection";
import "../styles/instagram.css";
import heroImage from "../assets/ROha1.jpg";
import showroomImage from "../assets/all-images/bg15.jpg";
import gemstoneImage from "../assets/all-images/bg11.jpg";
import craftImage from "../assets/all-images/bg16.jpg";
import miningImage from "../assets/miningwatch-portugal-YG0qc-e6hgg-unsplash.jpg";
import bg1 from "../assets/all-images/bg1.jpg";
import bg2 from "../assets/all-images/bg2.jpg";
import bg3 from "../assets/all-images/bg3.jpg";
import bg4 from "../assets/all-images/bg4.jpg";
import { AuthContext } from "../context/authContext";
import { getApiUrl, getMediaUrl, getWhatsappUrl, siteConfig } from "../utils/site";
import "../styles/home-luxury.css";

/* Animation helpers */
const fadeUp = { hidden:{opacity:0,y:36}, visible:{opacity:1,y:0,transition:{duration:0.72,ease:[0.25,0.46,0.45,0.94]}} };
const stagger = { visible:{transition:{staggerChildren:0.11}} };
const slideL = { hidden:{opacity:0,x:-48}, visible:{opacity:1,x:0,transition:{duration:0.78,ease:[0.25,0.46,0.45,0.94]}} };
const slideR = { hidden:{opacity:0,x:48}, visible:{opacity:1,x:0,transition:{duration:0.78,ease:[0.25,0.46,0.45,0.94]}} };

const Reveal = ({ children, variants=fadeUp, className="", delay=0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once:true, margin:"-72px" });
  const v = { ...variants, visible: { ...variants.visible, transition: { ...variants.visible?.transition, delay } } };
  return <motion.div ref={ref} className={className} initial="hidden" animate={inView?"visible":"hidden"} variants={v}>{children}</motion.div>;
};

const RevealGroup = ({ children, className="", delay=0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once:true, margin:"-72px" });
  const v = { ...stagger, visible: { ...stagger.visible, transition: { staggerChildren:0.11, delayChildren:delay } } };
  return <motion.div ref={ref} className={className} initial="hidden" animate={inView?"visible":"hidden"} variants={v}>{children}</motion.div>;
};

/* Static data */
const trustStats = [
  { value:"30+", label:"Years of trusted service" },
  { value:"Addis Ababa", label:"Ethiopia gemstone capital" },
  { value:"Worldwide", label:"International delivery" },
  { value:"Bespoke", label:"Custom jewellery crafted for you" },
];

const gemstoneCollections = [
  { title:"Ethiopian Opal", subtitle:"Nature most vivid gemstone", description:"Celebrated worldwide for extraordinary play-of-color, Ethiopian opals from Wollo and Shewa regions are among the most prized gemstones on earth.", image:bg1, tag:"Signature Collection" },
  { title:"Emerald and Ruby", subtitle:"Vivid African gemstones", description:"Sourced from Ethiopia mineral-rich highlands, our emeralds and rubies carry the deep saturated color that defines true luxury gemstone jewellery.", image:bg2, tag:"Heritage Collection" },
  { title:"Gold and Silver", subtitle:"Habesha-inspired metalwork", description:"Rooted in centuries of Ethiopian goldsmithing tradition, our gold and silver pieces blend ancient Habesha motifs with contemporary luxury design.", image:bg3, tag:"Artisan Collection" },
  { title:"Bridal and Wedding", subtitle:"Jewellery for life milestones", description:"From engagement rings to wedding sets, our bridal collection honors Ethiopian ceremony traditions while meeting the highest international luxury standards.", image:bg4, tag:"Bridal Collection" },
];

const whyRoha = [
  { icon:"✦", title:"Ethically Sourced", description:"Every gemstone is traced directly to its Ethiopian origin, ensuring ethical mining practices and authentic provenance." },
  { icon:"◈", title:"Master Craftsmanship", description:"Our artisans combine centuries-old Habesha goldsmithing techniques with modern precision to create pieces of enduring beauty." },
  { icon:"◇", title:"Certified Quality", description:"Each piece comes with full gemological certification, giving you complete confidence in the quality and authenticity of your purchase." },
  { icon:"◉", title:"Bespoke Design", description:"Work directly with our designers to create a completely unique piece that tells your personal story through Ethiopian gemstones." },
  { icon:"◈", title:"Global Delivery", description:"Secure insured worldwide shipping with full tracking. Your luxury jewellery arrives safely wherever you are in the world." },
  { icon:"✦", title:"Heritage Identity", description:"Every Roha piece carries the soul of Ethiopia, its landscapes, its people, and its millennia-old tradition of gemstone artistry." },
];

const testimonials = [
   { quote:"I commissioned a custom emerald necklace for my wife's anniversary. Roha team guided me through every step. The result was beyond our expectations.", author:"James Kumar", location:"New York, USA", rating:5, image:"https://i.pravatar.cc/150?img=12" },
  { quote:"As a musician, I appreciate artistry in all forms. Roha's Ethiopian opals capture the rhythm and soul of Ethiopia in gemstone form. Each piece feels like a musical composition.", author:"Zeritu Kebede", location:"Addis Ababa, Ethiopia", rating:5, image:"https://hintset.org/wp-content/uploads/2022/07/Zeritu_profile.jpg" },
  { quote:"Roha Jewellery transformed my vision into reality. The bespoke design service is truly world-class. Every detail was perfect.", author:"Maria Santos", location:"Dubai, UAE", rating:5, image:"https://i.pravatar.cc/150?img=9" },
  { quote:"The heritage and craftsmanship behind each piece is remarkable. I'm proud to wear Ethiopian artistry. Highly recommend Roha.", author:"David Chen", location:"Singapore", rating:5, image:"https://i.pravatar.cc/150?img=13" },
  { quote:"From consultation to delivery, the experience was seamless. The emerald pendant I received is stunning and the quality exceptional.", author:"Emma Williams", location:"Sydney, Australia", rating:5, image:"https://i.pravatar.cc/150?img=10" },
   { quote:"የኢትዮጵያ ታሪክ ከጥንት ጀምሮ ከከበሩ ድንጋዮች ውበትና ክብር ጋር የተሳሰረ ነው። Roha Jewellery ላይ ያገኘሁት gemstone እውነተኛ፣ ውብ እና ከፍተኛ ጥራት ያለው ነው። እውነተኛ የኢትዮጵያ ከበሩ ድንጋዮች ለሚፈልጉ Roha Jewellery ምርጥ ምርጫ ነው።", author:"Dr Rodas Taddes", location:"Ethiopia, Addis Ababa", rating:5, image:"https://andromedaacademy.org/wp-content/uploads/2024/06/Dr.-Rodas-scaled.jpg" },
  { quote:"As an artist, I see beauty in Ethiopian culture. Roha's jewellery captures that beauty in gemstones. My opal earrings are like wearable art that tells our Ethiopian story.", author:"Mahmoud Ahmed", location:"Addis Ababa, Ethiopia", rating:5, image:"https://www.thereporterethiopia.com/wp-content/uploads/2024/11/MAHMOUD-AHMED.jpg" },
  { quote:"The personalized service and exquisite craftsmanship exceeded all expectations. Wearing Roha jewellery feels like carrying a piece of Ethiopian heritage with elegance and pride.", author:"Aster Aweke", location:"Addis Ababa, Ethiopia", rating:5, image:"https://i.discogs.com/cVfztNLoskF90XQlMEc7tcVg33UXFr7hdFv92htY0Go/rs:fit/g:sm/q:40/h:300/w:300/czM6Ly9kaXNjb2dz/LWRhdGFiYXNlLWlt/YWdlcy9BLTQ1NDUy/My0xMjk3ODk1NTk5/LmpwZWc.jpeg" },
];

const gemstoneGuide = [
  { name:"Ethiopian Opal", origin:"Wollo and Shewa", color:"Multi-color play", rarity:"Exceptional", icon:"◈" },
  { name:"Ethiopian Emerald", origin:"Tigray Region", color:"Deep vivid green", rarity:"Rare", icon:"◇" },
  { name:"Ethiopian Ruby", origin:"Tigray Highlands", color:"Pigeon blood red", rarity:"Very Rare", icon:"◉" },
  { name:"Ethiopian Sapphire", origin:"Gambela Region", color:"Royal blue", rarity:"Rare", icon:"✦" },
];

const craftFeatures = [
  { icon:"✦", title:"Hand-selected gemstones", desc:"Every stone is personally inspected by our master gemologists before it enters our workshop." },
  { icon:"◈", title:"Traditional Habesha techniques", desc:"Ancient Ethiopian goldsmithing methods passed down through generations, preserved in every piece." },
  { icon:"◇", title:"Modern precision finishing", desc:"Contemporary tools and techniques ensure each piece meets the highest international luxury standards." },
];

/* WhatsApp SVG icon */
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Home = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    axios.get(getApiUrl("/api/products"))
      .then(r => setProducts(r.data.data || r.data || []))
      .catch(e => console.error(e))
      .finally(() => setLoadingProducts(false));
    axios.get(getApiUrl("/api/blogs"))
      .then(r => setBlogs(Array.isArray(r.data) ? r.data : r.data.blogs || []))
      .catch(e => console.error(e));
    axios.get(getApiUrl("/api/gallery"))
      .then(r => setGeneratedImages(Array.isArray(r.data) ? r.data : []))
      .catch(e => console.error(e));
  }, []);

  const featuredProducts = useMemo(() => products.slice(0, 6), [products]);
  const featuredBlogs    = useMemo(() => blogs.slice(0, 3), [blogs]);
  const galleryPreview   = useMemo(() => generatedImages.slice(0, 4), [generatedImages]);

  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "JewelryStore",
    name: siteConfig.name,
    url: siteConfig.url,
    description: "Ethiopia premier luxury jewellery brand. Ethiopian opal, emerald, ruby, sapphire, gold and silver jewellery. Handcrafted Habesha designs. International delivery.",
    address: { "@type": "PostalAddress", streetAddress: "Bole Dembel City Center GFS013", addressLocality: "Addis Ababa", addressCountry: "ET" },
    telephone: siteConfig.phones[0],
    email: siteConfig.email,
    priceRange: "4402$",
    openingHours: "Mo-Sa 09:00-19:00",
    areaServed: "Worldwide",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Ethiopian Gemstone Jewellery Collections",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Product", name: "Ethiopian Opal Jewellery" } },
        { "@type": "Offer", itemOffered: { "@type": "Product", name: "Ethiopian Emerald Jewellery" } },
        { "@type": "Offer", itemOffered: { "@type": "Product", name: "Habesha Gold Jewellery" } },
        { "@type": "Offer", itemOffered: { "@type": "Product", name: "Ethiopian Bridal Jewellery" } },
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>Roha Jewellery | Ethiopian Opal, Emerald and Luxury Gemstone Jewellery | Addis Ababa</title>
        <meta name="description" content="Roha Jewellery is Ethiopia premier luxury jewellery brand. Shop Ethiopian opal rings, emerald necklaces, Habesha gold jewellery, ruby and sapphire pieces. Handcrafted in Addis Ababa. Worldwide delivery. Bespoke custom jewellery design service." />
        <meta name="keywords" content="Ethiopian jewellery, Ethiopian opal, opal ring Ethiopia, emerald ring Ethiopia, Habesha jewellery, luxury jewellery Ethiopia, Ethiopian gemstone store, Addis Ababa jewellery, Ethiopian opal jewellery, premium African jewellery, Roha Jewellery, gold jewellery Ethiopia, silver jewellery Ethiopia, bespoke jewellery Ethiopia, Ethiopian bridal jewellery, wedding jewellery Ethiopia" />
        <link rel="canonical" href={siteConfig.url + "/"} />
        <meta property="og:title" content="Roha Jewellery | Ethiopian Luxury Gemstone Jewellery" />
        <meta property="og:description" content="Ethiopia premier luxury jewellery brand. Ethiopian opal, emerald, ruby, sapphire, gold and silver jewellery. Handcrafted Habesha designs." />
        <meta property="og:image" content={siteConfig.socialImage} />
        <meta property="og:url" content={siteConfig.url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
      </Helmet>

      {/* ── WhatsApp Float ── */}
      <div className="lux-whatsapp-float">
        <span className="lux-whatsapp-float__label">Chat with us</span>
        <a href={getWhatsappUrl("Hello Roha Jewellery, I would like to enquire about your gemstone collections.")} target="_blank" rel="noopener noreferrer" className="lux-whatsapp-float__btn" aria-label="WhatsApp">
          <WhatsAppIcon />
        </a>
      </div>

      <main className="roha-page">

        {/* SECTION 1: ENHANCED CINEMATIC HERO */}
        <section className="lux-hero" aria-label="Hero">
          {/* Animated Background */}
          <div className="lux-hero__bg">
            <img src={heroImage} alt="" loading="eager" aria-hidden="true" />
            <div className="lux-hero__bg-grain" aria-hidden="true" />
          </div>
          
          {/* Layered Overlays */}
          <div className="lux-hero__overlay" aria-hidden="true" />
          <div className="lux-hero__overlay2" aria-hidden="true" />
          <div className="lux-hero__vignette" aria-hidden="true" />

          {/* Floating Particles */}
          <div className="lux-hero__particles" aria-hidden="true">
            {[...Array(25)].map((_, i) => (
              <div 
                key={i} 
                className="lux-hero__particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 8}s`,
                  animationDuration: `${8 + Math.random() * 12}s`
                }}
              />
            ))}
          </div>

          <Container>
            <motion.div className="lux-hero__content"
              initial="hidden" animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } } }}
            >
              {/* Premium Badge */}
              <motion.div className="lux-hero__pre" variants={fadeUp}>
                <span className="lux-hero__pre-line" />
                <span className="lux-hero__pre-text">Roha Jewellery — Ethiopian Luxury</span>
                <span className="lux-hero__pre-line" />
              </motion.div>

              {/* Main Title with Gradient */}
              <motion.h1 className="lux-hero__title" variants={fadeUp}>
                Where Ethiopian<br />
                <span className="lux-hero__title-gradient">Gemstones</span> Become<br />
                Timeless <em>Art</em>
              </motion.h1>

              {/* Enhanced Subtitle */}
              <motion.p className="lux-hero__subtitle" variants={fadeUp}>
                Discover luminous Ethiopian opals, vivid emeralds, rubies, and handcrafted gold — each piece a celebration of Africa's finest gemstone heritage, designed for those who appreciate true luxury.
              </motion.p>

              {/* Stats Bar */}
              <motion.div className="lux-hero__stats" variants={fadeUp}>
                <div className="lux-hero__stat">
                  <span className="lux-hero__stat-value">15+</span>
                  <span className="lux-hero__stat-label">Years </span>
                </div>
                <div className="lux-hero__stat-divider" />
                <div className="lux-hero__stat">
                  <span className="lux-hero__stat-value">5000+</span>
                  <span className="lux-hero__stat-label">Happy Clients</span>
                </div>
                <div className="lux-hero__stat-divider" />
                {/* <div className="lux-hero__stat">
                  <span className="lux-hero__stat-value">50+</span>
                  <span className="lux-hero__stat-label">Countries</span>
                </div> */}
              </motion.div>

              {/* CTA Buttons */}
              <motion.div className="lux-hero__actions" variants={fadeUp}>
                <Link to="/products" className="roha-button roha-button--solid roha-button--large">
                  <span>Explore Collection</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
                <Link to="/design-request" className="roha-button roha-button--ghost roha-button--large">
                  <span>Bespoke Design</span>
                </Link>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div className="lux-hero__trust" variants={fadeUp}>
                <div className="lux-hero__trust-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                  </svg>
                  <span>Certified Gemstones</span>
                </div>
                <div className="lux-hero__trust-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Lifetime Warranty</span>
                </div>
                <div className="lux-hero__trust-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  {/* <span>Worldwide Shipping</span> */}
                </div>
              </motion.div>

              {/* Enhanced Signup Section */}
              {!authLoading && !user && (
                <motion.div className="lux-hero__signup" variants={fadeUp}>
                  {/* Animated background glow */}
                  <div className="lux-hero__signup-glow" aria-hidden="true" />
                  
                  <div className="lux-hero__signup-inner">
                    <div className="lux-hero__signup-header">
                      <div className="lux-hero__signup-icon-wrapper">
                        <div className="lux-hero__signup-icon">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="lux-hero__signup-icon-ring" aria-hidden="true" />
                      </div>
                      
                      <div className="lux-hero__signup-text">
                        <div className="lux-hero__signup-badge">
                          <span className="lux-hero__signup-badge-dot" />
                          Exclusive Access
                        </div>
                        <h3>Join Roha Jewellery</h3>
                        <p>Become part of our exclusive community and unlock a world of Ethiopian luxury</p>
                      </div>
                    </div>

                    <div className="lux-hero__signup-benefits">
                      <div className="lux-hero__signup-benefit">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Early access to new collections</span>
                      </div>
                      <div className="lux-hero__signup-benefit">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Personalized gemstone recommendations</span>
                      </div>
                      <div className="lux-hero__signup-benefit">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Save favorites & track orders</span>
                      </div>
                    </div>

                    <div className="lux-hero__signup-actions">
                      <Link to="/register" className="lux-hero__signup-btn lux-hero__signup-btn--primary">
                        <span>Create Your Account</span>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </Link>
                      <Link to="/login" className="lux-hero__signup-btn lux-hero__signup-btn--secondary">
                        <span>Already a member? <strong>Sign In</strong></span>
                      </Link>
                    </div>

                    <div className="lux-hero__signup-footer">
                      <span>Join 5,000+ jewellery lovers worldwide</span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Scroll Indicator */}
              <motion.div 
                className="lux-hero__scroll-indicator"
                variants={fadeUp}
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <span>Scroll to explore</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 4V16M10 16L6 12M10 16L14 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </motion.div>
            </motion.div>

            {/* Floating Side Badges */}
            <motion.div 
              className="lux-hero__side-badges"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
            >
              {[
                { icon: "◈", text: "Ethiopian Opals", desc: "World's finest" },
                { icon: "✦", text: "Habesha Craft", desc: "30+ years" },
                { icon: "◇", text: "Ethical Mining", desc: "100% traced" }
              ].map((badge, i) => (
                <motion.div 
                  key={badge.text} 
                  className="lux-hero__side-badge"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2 + i * 0.15, duration: 0.6 }}
                >
                  <span className="lux-hero__side-badge-icon">{badge.icon}</span>
                  <div className="lux-hero__side-badge-content">
                    <span className="lux-hero__side-badge-text">{badge.text}</span>
                    <span className="lux-hero__side-badge-desc">{badge.desc}</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </Container>
        </section>

        {/* ── Trust Strip ── */}
        <div className="lux-trust">
          <Container>
            <div className="lux-trust__inner">
              {trustStats.map(s => (
                <div key={s.label} className="lux-trust__item">
                  <span className="lux-trust__value">{s.value}</span>
                  <span className="lux-trust__label">{s.label}</span>
                </div>
              ))}
            </div>
          </Container>
        </div>

        {/* TRUST STRIP — always visible below hero */}
        <TrustBar variant="strip" />

        {/* SECTION 2: GEMSTONE COLLECTIONS */}
        <section className="roha-section">
          <Container>
            <RevealGroup>
              <motion.div className="roha-section__heading" variants={fadeUp}>
                <p className="roha-eyebrow">Our Collections</p>
                <h2>Jewellery Rooted in Ethiopian Heritage</h2>
                <p>From the fire of Ethiopian opals to the deep green of Tigray emeralds, every Roha collection tells the story of Africa most extraordinary gemstone legacy.</p>
              </motion.div>
            </RevealGroup>
            <RevealGroup>
              <motion.div className="lux-collections" variants={stagger}>
                {gemstoneCollections.map((col, i) => (
                  <motion.div key={col.title} className="lux-collection-card" variants={fadeUp}
                    whileHover={{ scale: 1.01 }} transition={{ duration: 0.3 }}
                  >
                    <img src={col.image} alt={col.title} loading={i < 2 ? "eager" : "lazy"} />
                    <div className="lux-collection-card__overlay" />
                    <div className="lux-collection-card__body">
                      <span className="lux-collection-card__tag">{col.tag}</span>
                      <h3 className="lux-collection-card__title">{col.title}</h3>
                      <p className="lux-collection-card__sub">{col.subtitle}</p>
                      <Link to="/products" className="lux-collection-card__link">
                        Explore {col.title} <span>&#8594;</span>
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </RevealGroup>
          </Container>
        </section>

        {/* SECTION 3: GEMSTONE GUIDE */}
        <section className="roha-section roha-section--soft">
          <Container>
            <RevealGroup>
              <motion.div className="roha-section__heading" variants={fadeUp}>
                <p className="roha-eyebrow">Ethiopian Gemstone Guide</p>
                <h2>The World's Most Extraordinary Gemstones</h2>
                <p>Ethiopia is home to some of the planet's most prized gemstones. Each stone carries millions of years of geological history and the warmth of African earth.</p>
              </motion.div>
            </RevealGroup>
            <RevealGroup>
              <motion.div className="lux-gemstone-grid" variants={stagger}>
                {gemstoneGuide.map(gem => (
                  <motion.div key={gem.name} className="lux-gem-card" variants={fadeUp}>
                    <span className="lux-gem-card__icon">{gem.icon}</span>
                    <h3 className="lux-gem-card__name">{gem.name}</h3>
                    <div className="lux-gem-card__row"><span>Origin</span><span>{gem.origin}</span></div>
                    <div className="lux-gem-card__row"><span>Color</span><span>{gem.color}</span></div>
                    <div className="lux-gem-card__row"><span>Rarity</span><span>{gem.rarity}</span></div>
                  </motion.div>
                ))}
              </motion.div>
            </RevealGroup>
          </Container>
        </section>

        {/* SECTION 4: FEATURED PRODUCTS */}
        <section className="roha-section" id="featured-collection" aria-labelledby="featured-heading">
          <Container>

            {/* ── Premium Section Header ── */}
            <Reveal>
              <div className="fc-header">
                <div className="fc-header__top">
                  <div className="fc-header__left">
                    <p className="roha-eyebrow">Featured Collection</p>
                    <h2 id="featured-heading" className="fc-header__title">
                      Handpicked Ethiopian<br />
                      <span className="fc-header__gold">Gemstone Pieces</span>
                    </h2>
                  </div>
                  <div className="fc-header__right">
                    <p className="fc-header__desc">
                      Each piece is ethically sourced, certified authentic, and crafted
                      by master artisans in Addis Ababa — delivered worldwide.
                    </p>
                    <div className="fc-header__actions">
                      <Link to="/products" className="roha-button roha-button--solid" aria-label="Browse full jewellery collection">
                        Browse All Pieces
                      </Link>
                      <Link to="/design-request" className="roha-button roha-button--ghost">
                        Custom Order
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Stats strip */}
                <div className="fc-stats" role="list" aria-label="Collection highlights">
                  {[
                    { value: '500+', label: 'Unique Pieces' },
                    { value: '30+', label: 'Years of Craft' },
                    { value: '50+', label: 'Countries Served' },
                    { value: '100%', label: 'Ethically Sourced' },
                    { value: 'GIA', label: 'Certified Gems' },
                  ].map(s => (
                    <div key={s.label} className="fc-stat" role="listitem">
                      <strong className="fc-stat__value">{s.value}</strong>
                      <span className="fc-stat__label">{s.label}</span>
                    </div>
                  ))}
                </div>

                {/* Category filter pills */}
                <div className="fc-filters" role="navigation" aria-label="Filter by gemstone category">
                  {['All', 'Opal', 'Ruby', 'Emerald', 'Sapphire', 'Gold', 'Bridal'].map(cat => (
                    <Link
                      key={cat}
                      to={cat === 'All' ? '/products' : `/products?category=${cat}`}
                      className="fc-filter-pill"
                    >
                      {cat === 'Opal' && '💎 '}
                      {cat === 'Ruby' && '🔴 '}
                      {cat === 'Emerald' && '💚 '}
                      {cat === 'Sapphire' && '🔵 '}
                      {cat === 'Gold' && '✨ '}
                      {cat === 'Bridal' && '💍 '}
                      {cat}
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* ── AI Generator Banner ── */}
            <Reveal>
              <div className="ai-banner" role="region" aria-label="Roha AI Image Generator">
                {/* Glow orbs */}
                <div className="ai-banner__orb ai-banner__orb--1" aria-hidden="true" />
                <div className="ai-banner__orb ai-banner__orb--2" aria-hidden="true" />

                <div className="ai-banner__inner">
                  {/* Left — content */}
                  <div className="ai-banner__content">
                    {/* Live badge */}
                    <div className="ai-banner__badge" aria-label="AI feature">
                      <span className="ai-banner__badge-pulse" aria-hidden="true" />
                      <span className="ai-banner__badge-icon" aria-hidden="true">✦</span>
                      Powered by Roha Image Generator
                    </div>

                    <h2 className="ai-banner__title">
                      Design Your Dream<br />
                      <span className="ai-banner__title-gold">Jewellery with AI</span>
                    </h2>

                    <p className="ai-banner__desc">
                      Describe your vision — a gemstone, a style, an occasion — and our AI instantly
                      generates a stunning jewellery concept crafted around Ethiopian gemstones.
                    </p>

                    {/* Feature list */}
                    <ul className="ai-banner__features" aria-label="AI Generator features">
                      {[
                        { icon: '⚡', text: 'Instant AI-generated jewellery visuals' },
                        { icon: '💎', text: 'Ethiopian opal, ruby, emerald & sapphire styles' },
                        { icon: '🔗', text: 'Save and share your designs' },
                      ].map(f => (
                        <li key={f.text} className="ai-banner__feature">
                          <span className="ai-banner__feature-icon" aria-hidden="true">{f.icon}</span>
                          <span>{f.text}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTAs */}
                    <div className="ai-banner__actions">
                      <Link to="/generate-image" className="ai-banner__cta-primary">
                        <span className="ai-banner__cta-icon" aria-hidden="true">✦</span>
                        Try Roha Image Generator
                        <span className="ai-banner__cta-arrow" aria-hidden="true">→</span>
                      </Link>
                      <Link to="/ai-gallery" className="ai-banner__cta-secondary">
                        View AI Gallery
                      </Link>
                    </div>

                    <p className="ai-banner__note">
                      <span aria-hidden="true">🔓</span> Free to use · No account required to preview
                    </p>
                  </div>

                  {/* Right — visual */}
                  <div className="ai-banner__visual" aria-hidden="true">
                    <div className="ai-banner__screen">
                      <div className="ai-banner__screen-header">
                        <span className="ai-banner__screen-dot" />
                        <span className="ai-banner__screen-dot" />
                        <span className="ai-banner__screen-dot" />
                        <span className="ai-banner__screen-title">Roha AI Generator</span>
                      </div>
                      <div className="ai-banner__screen-body">
                        <div className="ai-banner__prompt-box">
                          <span className="ai-banner__prompt-label">Your prompt</span>
                          <p className="ai-banner__prompt-text">
                            "A delicate Ethiopian opal ring with rose gold filigree and diamond halo..."
                            <span className="ai-banner__cursor">|</span>
                          </p>
                        </div>
                        <div className="ai-banner__cards">
                          {[
                            { icon: '💎', name: 'Ethiopian Opal Ring', sub: 'Vivid play-of-color · 18K gold' },
                            { icon: '🔴', name: 'Ruby Pendant', sub: 'Pigeon blood · Habesha design' },
                            { icon: '💚', name: 'Emerald Earrings', sub: 'Tigray emerald · Bridal set' },
                            { icon: '🔵', name: 'Sapphire Bracelet', sub: 'Royal blue · Contemporary' },
                          ].map((card, i) => (
                            <div key={card.name} className={`ai-banner__card ai-banner__card--${i + 1}`}>
                              <span className="ai-banner__card-icon">{card.icon}</span>
                              <div>
                                <strong>{card.name}</strong>
                                <p>{card.sub}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Product grid */}
            <RevealGroup>
              <motion.div className="product-grid" variants={stagger}>
                {loadingProducts ? (
                  <ProductSkeletonGrid count={6} />
                ) : featuredProducts.length ? (
                  featuredProducts.map(product => (
                    <motion.div key={product.uuid || product.id} variants={fadeUp}>
                      <ProductItem item={product} />
                    </motion.div>
                  ))
                ) : (
                  <div className="roha-empty-state">No products available yet.</div>
                )}
              </motion.div>
            </RevealGroup>

            {/* Bottom CTA row */}
            <Reveal>
              <div className="featured-bottom-row">
                <div className="featured-bottom-row__stats">
                  <span>500+ unique pieces</span>
                  <span className="featured-bottom-row__dot">·</span>
                  <span>Ethically sourced</span>
                  <span className="featured-bottom-row__dot">·</span>
                  <span>Worldwide delivery</span>
                </div>
                <Link to="/products" className="roha-button roha-button--solid" aria-label="Browse all jewellery">
                  Browse All Jewellery
                </Link>
              </div>
            </Reveal>
          </Container>
        </section>

        {/* SECTION 5: WHY ROHA */}
        <section className="roha-section roha-section--soft">
          <Container>
            <RevealGroup>
              <motion.div className="roha-section__heading" variants={fadeUp}>
                <p className="roha-eyebrow">Why Roha Jewellery</p>
                <h2>The Roha Promise — Beauty, Trust, Heritage</h2>
                <p>We are more than a jewellery store. We are Ethiopia's gateway to the world's finest gemstone artistry, built on 30 years of trust and craftsmanship.</p>
              </motion.div>
            </RevealGroup>
            <RevealGroup>
              <motion.div className="lux-why-grid" variants={stagger}>
                {whyRoha.map(w => (
                  <motion.div key={w.title} className="lux-why-card" variants={fadeUp}>
                    <span className="lux-why-card__icon">{w.icon}</span>
                    <h3 className="lux-why-card__title">{w.title}</h3>
                    <p className="lux-why-card__desc">{w.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </RevealGroup>
          </Container>
        </section>

        {/* SECTION 6: CRAFTSMANSHIP EDITORIAL */}
        <section className="roha-section">
          <Container>
            <div className="lux-craft">
              <Reveal variants={slideL}>
                <div className="lux-craft__image-wrap">
                  <div className="lux-craft__image-main">
                    <img src={craftImage} alt="Roha Jewellery master craftsmanship" loading="lazy" />
                  </div>
                  <div className="lux-craft__image-accent">
                    <img src={gemstoneImage} alt="Ethiopian gemstone detail" loading="lazy" />
                  </div>
                </div>
              </Reveal>
              <Reveal variants={slideR}>
                <div className="lux-craft__content">
                  <p className="roha-eyebrow">Master Craftsmanship</p>
                  <h2>Every Gemstone Deserves Care, Context, and Meaning</h2>
                  <p style={{color:'var(--color-muted)',lineHeight:1.8}}>
                    At Roha, our master artisans blend centuries of Ethiopian goldsmithing tradition with modern precision. Each piece is a living testament to Africa's extraordinary gemstone heritage.
                  </p>
                  <div className="lux-craft__stat-row">
                    <div className="lux-craft__stat"><strong>30+</strong><span>Years of expertise</span></div>
                    <div className="lux-craft__stat"><strong>500+</strong><span>Unique designs</span></div>
                    <div className="lux-craft__stat"><strong>100%</strong><span>Ethically sourced</span></div>
                    <div className="lux-craft__stat"><strong>50+</strong><span>Countries served</span></div>
                  </div>
                  <div className="lux-craft__features">
                    {craftFeatures.map(f => (
                      <div key={f.title} className="lux-craft__feature">
                        <span className="lux-craft__feature-icon">{f.icon}</span>
                        <div><h4>{f.title}</h4><p>{f.desc}</p></div>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:'2rem',display:'flex',gap:'1rem',flexWrap:'wrap'}}>
                    <Link to="/about" className="roha-button roha-button--solid">Our Story</Link>
                    <Link to="/designs" className="roha-button roha-button--ghost">View Designs</Link>
                  </div>
                </div>
              </Reveal>
            </div>
          </Container>
        </section>

        {/* SECTION 7: ROHA MINING - ETHIOPIAN GEMSTONE SOURCES */}
        <section className="lux-mining">
          <div className="lux-mining__bg">
            <img src={miningImage} alt="Roha Mining Operations in Ethiopia" loading="lazy" />
            <div className="lux-mining__overlay"></div>
            <div className="lux-mining__overlay-gradient"></div>
          </div>

          {/* Floating Mining Particles */}
          <div className="lux-mining__particles">
            {Array.from({ length: 15 }).map((_, i) => (
              <div 
                key={i} 
                className="lux-mining__particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 8}s`
                }}
              />
            ))}
          </div>

          <Container>
            <Reveal>
              <div className="lux-mining__content">
                <div className="lux-mining__badge">
                  <span className="lux-mining__badge-icon">◈</span>
                  <span>From Earth to Elegance</span>
                </div>

                <h2 className="lux-mining__title">
                  Our Own Gold &<br />
                  <span className="lux-mining__title-accent">Gemstone Mines</span><br />
                  in Ethiopia
                </h2>

                <p className="lux-mining__subtitle">
                  Roha Jewellery owns and operates ethical mining sites across Ethiopia's most 
                  gemstone-rich regions. From the opal fields of Wollo to the gold deposits of 
                  the highlands, we control every step of the journey—from earth to your hands.
                </p>

                <div className="lux-mining__features">
                  <div className="lux-mining__feature">
                    <div className="lux-mining__feature-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <path d="M20 5L5 15L20 25L35 15L20 5Z" fill="currentColor" opacity="0.3"/>
                        <path d="M20 5L5 15L20 25M20 5L35 15L20 25M20 5V25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5 15V25L20 35L35 25V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M20 25V35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h4>Ethiopian Opal Mines</h4>
                    <p>Direct access to Wollo and Shewa opal deposits, the world's finest play-of-color gemstones</p>
                  </div>

                  <div className="lux-mining__feature">
                    <div className="lux-mining__feature-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <circle cx="20" cy="20" r="12" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <circle cx="20" cy="20" r="8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        <path d="M12 12L28 28M28 12L12 28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <h4>Gold Mining Operations</h4>
                    <p>Highland gold deposits refined to 22K and 24K purity for premium jewellery</p>
                  </div>

                  <div className="lux-mining__feature">
                    <div className="lux-mining__feature-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect x="8" y="12" width="24" height="20" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <rect x="12" y="16" width="16" height="12" rx="1" fill="currentColor" opacity="0.3"/>
                        <path d="M16 8L24 8M16 8V12M24 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <h4>Emerald & Precious Stones</h4>
                    <p>Tigray region emeralds, rubies, and sapphires sourced directly from our mines</p>
                  </div>
                </div>

                <div className="lux-mining__stats">
                  <div className="lux-mining__stat">
                    <span className="lux-mining__stat-value">5+</span>
                    <span className="lux-mining__stat-label">Mining Sites</span>
                  </div>
                  <div className="lux-mining__stat">
                    <span className="lux-mining__stat-value">100%</span>
                    <span className="lux-mining__stat-label">Ethical Sourcing</span>
                  </div>
                  <div className="lux-mining__stat">
                    <span className="lux-mining__stat-value">200+</span>
                    <span className="lux-mining__stat-label">Local Miners</span>
                  </div>
                </div>

                <div className="lux-mining__actions">
                  <Link to="/products" className="lux-mining__btn lux-mining__btn--primary">
                    <span>Shop Mine-to-Market Collection</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                  <Link to="/about" className="lux-mining__btn lux-mining__btn--secondary">
                    <span>Learn About Our Mining</span>
                  </Link>
                </div>

                <div className="lux-mining__certifications">
                  <div className="lux-mining__cert">
                    <span className="lux-mining__cert-icon">✓</span>
                    <span>Fair Trade Certified</span>
                  </div>
                  <div className="lux-mining__cert">
                    <span className="lux-mining__cert-icon">✓</span>
                    <span>Ethical Mining Practices</span>
                  </div>
                  <div className="lux-mining__cert">
                    <span className="lux-mining__cert-icon">✓</span>
                    <span>Environmental Responsibility</span>
                  </div>
                  <div className="lux-mining__cert">
                    <span className="lux-mining__cert-icon">✓</span>
                    <span>Community Development</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </Container>
        </section>

        {/* SECTION 8: TESTIMONIALS - INFINITE SCROLL */}
        <section className="roha-section roha-section--soft">
          <Container>
            <RevealGroup>
              <motion.div className="roha-section__heading" variants={fadeUp}>
                <p className="roha-eyebrow">Client Stories</p>
                <h2>Trusted by Jewellery Lovers Worldwide</h2>
                <p>From London to Tokyo, our clients share their experiences with Roha's Ethiopian gemstone jewellery.</p>
              </motion.div>
            </RevealGroup>
          </Container>
          
          {/* Infinite Scroll Testimonials */}
          <div className="lux-testimonials-scroll">
            <div className="lux-testimonials-track">
              {/* First set */}
              {testimonials.map((t, index) => (
                <div key={`testimonial-1-${index}`} className="lux-testimonial-card-scroll">
                  <div className="lux-testimonial-card-scroll__stars">
                    {Array.from({length:t.rating}).map((_,i) => <span key={i}>★</span>)}
                  </div>
                  <p className="lux-testimonial-card-scroll__quote">"{t.quote}"</p>
                  <div className="lux-testimonial-card-scroll__author">
                    <img 
                      src={t.image} 
                      alt={t.author}
                      className="lux-testimonial-card-scroll__avatar"
                      loading="lazy"
                    />
                    <div>
                      <div className="lux-testimonial-card-scroll__name">{t.author}</div>
                      <div className="lux-testimonial-card-scroll__loc">{t.location}</div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {testimonials.map((t, index) => (
                <div key={`testimonial-2-${index}`} className="lux-testimonial-card-scroll">
                  <div className="lux-testimonial-card-scroll__stars">
                    {Array.from({length:t.rating}).map((_,i) => <span key={i}>★</span>)}
                  </div>
                  <p className="lux-testimonial-card-scroll__quote">"{t.quote}"</p>
                  <div className="lux-testimonial-card-scroll__author">
                    <img 
                      src={t.image} 
                      alt={t.author}
                      className="lux-testimonial-card-scroll__avatar"
                      loading="lazy"
                    />
                    <div>
                      <div className="lux-testimonial-card-scroll__name">{t.author}</div>
                      <div className="lux-testimonial-card-scroll__loc">{t.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 9: AI GALLERY */}
        <section className="roha-section">
          <Container>
            <RevealGroup>
              <motion.div className="roha-section__heading roha-section__heading--row" variants={fadeUp}>
                <div>
                  <p className="roha-eyebrow">Roha AI Design Gallery</p>
                  <h2>Jewellery Inspiration, Powered by AI</h2>
                </div>
                <Link to="/ai-gallery" className="roha-text-link">Open gallery</Link>
              </motion.div>
            </RevealGroup>
            <RevealGroup>
              <motion.div className="roha-gallery-preview" variants={stagger}>
                {galleryPreview.length ? galleryPreview.map(image => (
                  <motion.article key={image.id} className="roha-gallery-preview__card" variants={fadeUp}>
                    <img src={getMediaUrl(image.image_url)} alt={image.prompt || "AI jewellery concept"} loading="lazy" />
                    <div>
                      <strong>{image.prompt || "AI gemstone concept"}</strong>
                      <p>{image.created_at ? new Date(image.created_at).toLocaleDateString() : "Creative inspiration"}</p>
                    </div>
                  </motion.article>
                )) : (
                  <div className="roha-empty-state">Gallery items will appear here when available.</div>
                )}
              </motion.div>
            </RevealGroup>
          </Container>
        </section>

        {/* SECTION 10: ROHA COURSE */}
        <section className="roha-section roha-section--soft">
          <Container>
            <div className="roha-section__split">
              <Reveal variants={slideL}>
                <div className="roha-showcase-card">
                  <img src={showroomImage} alt="Roha Jewellery course and training" loading="lazy" />
                </div>
              </Reveal>
              <Reveal variants={slideR}>
                <div>
                  <p className="roha-eyebrow">Roha Course</p>
                  <h2>Learn the Art of Ethiopian Gemstones</h2>
                  <p style={{color:'var(--color-muted)',lineHeight:1.8,marginBottom:'1.5rem'}}>
                    Our Roha Course guides you through gemstone mining, jewellery making, and the artistry behind every piece. Perfect for future makers, sellers, and anyone passionate about Ethiopian gemstones.
                  </p>
                  <div className="roha-list-card">
                    <div><strong>Gemstone Identification</strong><p>Learn to identify and grade Ethiopian opals, emeralds, rubies, and sapphires.</p></div>
                    <div><strong>Jewellery Design</strong><p>Master the principles of luxury jewellery design rooted in Habesha tradition.</p></div>
                    <div><strong>Business of Gemstones</strong><p>Understand the global gemstone market and how to build an authentic brand.</p></div>
                  </div>
                  <div style={{marginTop:'2rem',display:'flex',gap:'1rem',flexWrap:'wrap'}}>
                    <Link to="/course" className="roha-button roha-button--solid">Explore Roha Course</Link>
                    <Link to="/contact" className="roha-button roha-button--ghost">Ask about training</Link>
                  </div>
                </div>
              </Reveal>
            </div>
          </Container>
        </section>

        {/* SECTION 11: PROFESSIONAL BLOG JOURNAL */}
        {!!featuredBlogs.length && (
          <section className="roha-section lux-blog-section">
            <Container>
              <RevealGroup>
                <motion.div className="roha-section__heading roha-section__heading--center" variants={fadeUp}>
                  <div className="lux-blog-badge">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 7H17M7 12H17M7 17H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Roha Journal</span>
                  </div>
                  <h2>Stories, Knowledge & Inspiration</h2>
                  <p>Explore the world of Ethiopian gemstones, craftsmanship, and luxury jewellery through our curated editorial content.</p>
                  <Link to="/blogs" className="lux-blog-view-all">
                    <span>View All Articles</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                </motion.div>
              </RevealGroup>
              <RevealGroup>
                <motion.div className="lux-blog-grid" variants={stagger}>
                  {featuredBlogs.map((blog, index) => (
                    <motion.article key={blog.id || index} className="lux-blog-card" variants={fadeUp}>
                      <Link to={`/blogs/${blog.id}`} className="lux-blog-card__image-wrapper">
                        <img
                          src={getMediaUrl(blog.image_url)}
                          alt={blog.title || "Roha Jewellery article"}
                          className="lux-blog-card__image"
                          loading="lazy"
                          onError={e => { e.currentTarget.onerror=null; e.currentTarget.src="/image.jpg"; }}
                        />
                        <div className="lux-blog-card__overlay" />
                        <div className="lux-blog-card__category">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                          </svg>
                          <span>Featured</span>
                        </div>
                      </Link>
                      <div className="lux-blog-card__body">
                        <div className="lux-blog-card__meta">
                          <span className="lux-blog-card__date">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                              <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            {blog.created_at ? new Date(blog.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent'}
                          </span>
                          <span className="lux-blog-card__read-time">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
                              <path d="M12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" fill="currentColor"/>
                            </svg>
                            5 min read
                          </span>
                        </div>
                        <h3 className="lux-blog-card__title">
                          <Link to={`/blogs/${blog.id}`}>{blog.title || "Roha Jewellery Article"}</Link>
                        </h3>
                        <p className="lux-blog-card__excerpt">
                          {String(blog.excerpt || blog.content || "").replace(/<[^>]+>/g,"").slice(0,120) || "Discover the artistry and heritage behind Ethiopian gemstone jewellery."}...
                        </p>
                        <Link to={`/blogs/${blog.id}`} className="lux-blog-card__link">
                          <span>Read Article</span>
                          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </Link>
                      </div>
                    </motion.article>
                  ))}
                </motion.div>
              </RevealGroup>
            </Container>
          </section>
        )}

        {/* SECTION 12: SEO CONTENT */}
        <section className="roha-section roha-section--soft">
          <Container>
            <RevealGroup>
              <motion.div className="roha-section__heading" variants={fadeUp}>
                <p className="roha-eyebrow">Ethiopian Gemstone Heritage</p>
                <h2>Why Ethiopian Gemstones Are the World's Finest</h2>
                <p>Ethiopia's unique geology produces gemstones of extraordinary quality. From the volcanic highlands of Wollo to the mineral-rich soils of Tigray, every stone tells a story of ancient earth and human artistry.</p>
              </motion.div>
            </RevealGroup>
            <RevealGroup>
              <motion.div className="roha-seo-panels" variants={stagger}>
                <motion.article className="roha-feature-card" variants={fadeUp}>
                  <h3>Ethiopian Opal — Nature's Masterpiece</h3>
                  <p>Ethiopian opals from the Wollo and Shewa regions display a play-of-color unmatched anywhere on earth. Roha sources only the finest specimens, certified and ethically mined.</p>
                </motion.article>
                <motion.article className="roha-feature-card" variants={fadeUp}>
                  <h3>Habesha Gold Craftsmanship</h3>
                  <p>Ethiopia's goldsmithing tradition spans millennia. Our Habesha-inspired designs honor this heritage while meeting the highest standards of contemporary luxury jewellery design.</p>
                </motion.article>
                <motion.article className="roha-feature-card" variants={fadeUp}>
                  <h3>Addis Ababa — Africa's Gemstone Capital</h3>
                  <p>Located in the heart of Addis Ababa, Roha Jewellery is your trusted gateway to authentic Ethiopian gemstones, serving clients from Ethiopia and across the world.</p>
                </motion.article>
              </motion.div>
            </RevealGroup>
          </Container>
        </section>

        {/* PACKAGING SECTION */}
        <PackagingSection />

        {/* TRUST CARDS */}
        <section className="roha-section roha-section--soft" aria-labelledby="trust-heading">
          <Container>
            <Reveal>
              <div className="roha-section__heading">
                <p className="roha-eyebrow">Our Guarantees</p>
                <h2 id="trust-heading">Every Order, Every Promise</h2>
                <p>We stand behind every piece we sell with industry-leading guarantees.</p>
              </div>
            </Reveal>
            <Reveal>
              <TrustBar variant="cards" />
            </Reveal>
          </Container>
        </section>

        {/* CUSTOMER REVIEWS */}
        <section className="roha-section" aria-labelledby="reviews-heading">
          <Container>
            <RevealGroup>
              <motion.div className="roha-section__heading" variants={fadeUp}>
                <p className="roha-eyebrow">Customer Reviews</p>
                <h2 id="reviews-heading">What Our Clients Say</h2>
                <p>Verified reviews from jewellery lovers across the world.</p>
              </motion.div>
            </RevealGroup>
            <ReviewsSection />
          </Container>
        </section>

        {/* INSTAGRAM FEED */}
        <section className="roha-section" aria-labelledby="instagram-heading">
          <Container>
            <Reveal>
              <div className="roha-section__heading roha-section__heading--row">
                <div>
                  <p className="roha-eyebrow">Follow Our Journey</p>
                  <h2 id="instagram-heading">Roha on Instagram</h2>
                  <p style={{ color: 'var(--color-muted)', maxWidth: 480, margin: '0.5rem 0 0' }}>
                    Real pieces, real craftsmanship — follow <strong style={{ color: 'var(--color-gold)' }}>@rohajewellery16</strong> for daily inspiration.
                  </p>
                </div>
                <a
                  href="https://www.instagram.com/rohajewellery16/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="roha-button roha-button--ghost"
                  aria-label="Follow Roha Jewellery on Instagram"
                >
                  Follow @rohajewellery16
                </a>
              </div>
            </Reveal>
            <Reveal>
              <InstagramSection compact={true} />
            </Reveal>
          </Container>
        </section>

        {/* SECTION 13: VISIT US — CONTACT + MAP */}        <section className="roha-section roha-section--soft">
          <Container>
            <Reveal>
              <div className="home-contact-section">
                {/* Left — info */}
                <div className="home-contact-info">
                  <p className="roha-eyebrow">Visit Our Showroom</p>
                  <h2>Come See Us in<br /><span className="home-contact-gold">Addis Ababa</span></h2>
                  <p className="home-contact-desc">
                    Experience Ethiopia's finest gemstone jewellery in person. Our Bole Dembel showroom
                    offers private consultations, gemstone viewings, and bespoke design sessions.
                  </p>

                  <div className="home-contact-details">
                    <div className="home-contact-detail">
                      <span className="home-contact-detail__icon">📍</span>
                      <div>
                        <strong>Showroom Address</strong>
                        <p>Bole Dembel City Center<br />Addis Ababa, Ethiopia</p>
                      </div>
                    </div>
                    <div className="home-contact-detail">
                      <span className="home-contact-detail__icon">🕐</span>
                      <div>
                        <strong>Opening Hours</strong>
                        <p>Monday – Saturday<br />9:00 AM – 7:00 PM</p>
                      </div>
                    </div>
                    <div className="home-contact-detail">
                      <span className="home-contact-detail__icon">📞</span>
                      <div>
                        <strong>Call or WhatsApp</strong>
                        <p>+251 910 387 575<br />+251 923 160 323</p>
                      </div>
                    </div>
                    <div className="home-contact-detail">
                      <span className="home-contact-detail__icon">✉️</span>
                      <div>
                        <strong>Email Us</strong>
                        <p>info@rohajewellery.com</p>
                      </div>
                    </div>
                  </div>

                  <div className="home-contact-actions">
                    <Link to="/contact" className="roha-button roha-button--solid">
                      Contact Us
                    </Link>
                    <a
                      href={getWhatsappUrl("Hello Roha Jewellery, I'd like to visit your showroom.")}
                      target="_blank" rel="noopener noreferrer"
                      className="roha-button roha-button--ghost"
                    >
                      WhatsApp Us
                    </a>
                  </div>
                </div>

                {/* Right — map */}
                <div className="home-contact-map">
                  <div className="home-contact-map__frame">
                    <iframe
                      title="Roha Jewellery Showroom — Bole Dembel City Center"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3940.5!2d38.7946!3d9.0105!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x164b85cdef4a5b3d%3A0x8a9b2c1e4f6d7e8a!2sBole%20Dembel%20City%20Center!5e0!3m2!1sen!2set!4v1700000000000!5m2!1sen!2set"
                      width="100%" height="100%"
                      style={{ border: 0 }}
                      allowFullScreen="" loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                  <a
                    href="https://maps.app.goo.gl/PpVToHGKCwX2AfyMA"
                    target="_blank" rel="noopener noreferrer"
                    className="home-contact-map__link"
                  >
                    <span>📍 Open in Google Maps</span>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </div>
              </div>
            </Reveal>
          </Container>
        </section>

        {/* SECTION 14: CTA BANNER */}
        <section className="roha-section">
          <Container>
            <Reveal>
              <div className="lux-cta-banner">
                <h2 className="lux-cta-banner__title">Begin Your Roha Jewellery Journey</h2>
                <p className="lux-cta-banner__sub">
                  Discover Ethiopia's finest gemstone jewellery. Shop our collections, commission a bespoke piece, or visit us in Addis Ababa for a private consultation.
                </p>
                <div className="lux-cta-banner__actions">
                  <Link to="/products" className="roha-button roha-button--solid">Shop Collection</Link>
                  <Link to="/design-request" className="roha-button roha-button--ghost">Bespoke Design</Link>
                  <a href={getWhatsappUrl("Hello Roha Jewellery, I would like a private consultation.")} target="_blank" rel="noopener noreferrer" className="roha-button roha-button--ghost">
                    Private Consultation
                  </a>
                </div>
              </div>
            </Reveal>
          </Container>
        </section>

      </main>
    </>
  );
};

export default Home;
